import { Space } from 'antd';
import { useState } from 'react';
import { EditOutlined } from '@ant-design/icons';
import ConfirmationModal from 'components/new-ui/composed/modal/confirmation-modal/ConfirmationModal';
import LabelRequiredSymbol from 'components/new-ui/base/label/LabelRequiredSymbol';
import { StyledTextArea } from 'components/new-ui/base/text-area/styles';
import { ErrorLabel } from 'components/new-ui/composed/formInput/styles';
import AmendmentSummaryTable from './AmendmentSummaryTable';
import { AmendmentChange } from './utils';
import { ManifestDeclarationsResponse } from 'store/manifests/types';
import NonAmendableDeclarationsSection from './NonAmendableDeclarationsSection';

type Props = {
    open: boolean;
    data?: ManifestDeclarationsResponse[];
    changes: AmendmentChange[];
    requiresAmendment?: boolean;
    onClose: () => void;
    onConfirm: (reason: string | undefined) => Promise<void>;
};

const AmendmentSummaryModal = ({ open, changes, data, requiresAmendment, onClose, onConfirm }: Props) => {
    const [amendmentReason, setAmendmentReason] = useState('');
    const [isFormTouched, setIsFormTouched] = useState(false);

    const isFormValid = !requiresAmendment || Boolean(amendmentReason.trim());

    return (
        <ConfirmationModal
            width={800}
            title={requiresAmendment ? 'Amendment Summary' : 'Changes Summary'}
            confirmButtonText={requiresAmendment ? 'Submit Bulk Amendment' : 'Edit Manifest'}
            confirmationButtonDisabled={!isFormValid}
            cancelButtonText="Go Back"
            icon={<EditOutlined />}
            onConfirm={async () => {
                await onConfirm(requiresAmendment ? amendmentReason : undefined);
                setAmendmentReason('');
                setIsFormTouched(false);
            }}
            open={open}
            onClose={onClose}
            onCancel={onClose}
            content={
                <Space style={{ width: '100%' }} size={24} direction="vertical">
                    {!!data?.length && <NonAmendableDeclarationsSection data={data} />}

                    <div>
                        <div style={{ marginBottom: 8, fontWeight: 600 }}>Fields Changed</div>
                        <AmendmentSummaryTable
                            changes={changes}
                            rowKey="field"
                            pagination={{
                                pageSize: 10,
                                position: ['bottomCenter'],
                                hideOnSinglePage: true,
                            }}
                        />
                    </div>

                    {requiresAmendment && (
                        <div>
                            <LabelRequiredSymbol /> <label htmlFor="amendment-reason">Amendment Reason</label>
                            <StyledTextArea
                                id="amendment-reason"
                                placeholder="Enter amendment reason"
                                rows={4}
                                style={{ marginTop: 6 }}
                                value={amendmentReason}
                                onChange={(e) => setAmendmentReason(e.target.value)}
                                onBlur={() => setIsFormTouched(true)}
                                $hasError={!isFormValid && isFormTouched}
                            />
                            <div>
                                {!isFormValid && isFormTouched && (
                                    <ErrorLabel>Amendment reason is required.</ErrorLabel>
                                )}
                            </div>
                        </div>
                    )}
                </Space>
            }
        />
    );
};

export default AmendmentSummaryModal;
